import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AppWindow, Braces, ChevronLeft, Copy, FileText, Loader2, Plus, Radio, Route, Save, Settings2, Terminal, Trash2,
  Workflow as WorkflowIcon,
} from 'lucide-react'
import {
  notiApi, type Channel, type Destination, type DestinationInput, type ParamKV, type PayloadSchema, type Workflow,
} from '../lib/api-noti'
import { CardTabs } from '../components/CardTabs'
import { Toggle } from '../components/Toggle'
import { KvEditor } from './ClientDetailPage'
import { CHANNEL_META } from './ChannelsPage'

const TABS = ['General', 'Parameters', 'Destinations', 'Templates', 'cURL'] as const

export default function WorkflowDetailPage() {
  const { workflowKey } = useParams({ strict: false }) as { workflowKey: string }
  const [tab, setTab] = useState<(typeof TABS)[number]>('General')
  const wf = useQuery({ queryKey: ['noti-workflow', workflowKey], queryFn: () => notiApi.getWorkflow(workflowKey) })

  if (wf.isLoading) return <div className="panel">กำลังโหลด…</div>
  if (wf.isError || !wf.data) return <div className="panel error">{(wf.error as Error)?.message ?? 'ไม่พบ workflow'}</div>
  const w = wf.data

  return (
    <>
      <div className="page-head">
        <h2>
          <Link to="/workflows" className="back-link"><ChevronLeft size={18} /></Link>
          <WorkflowIcon size={20} /> {w.name}
          <code className="head-code">{w.key}</code>
          {w.critical && <span className="pill fail">critical</span>}
          {!w.active && <span className="pill warn">inactive</span>}
        </h2>
      </div>

      <CardTabs<(typeof TABS)[number]>
        tabs={[
          { key: 'General', label: 'General', icon: Settings2 },
          { key: 'Parameters', label: 'Parameters', icon: Braces, badge: Object.keys(w.payloadSchema ?? {}).length || undefined },
          { key: 'Destinations', label: 'Destinations', icon: Route, badge: (w.destinations ?? []).length || undefined, badgeTone: 'ok' },
          { key: 'Templates', label: 'Templates', icon: FileText, badge: (w.destinations ?? []).length || undefined },
          { key: 'cURL', label: 'cURL', icon: Terminal },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'General' && <GeneralTab w={w} />}
      {tab === 'Parameters' && <ParametersTab w={w} />}
      {tab === 'Destinations' && <DestinationsTab w={w} />}
      {tab === 'Templates' && <TemplatesTab w={w} />}
      {tab === 'cURL' && <CurlTab w={w} />}
    </>
  )
}

// ── General: name/active/critical + allowed source clients ───────────────────
function GeneralTab({ w }: { w: Workflow }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const clients = useQuery({ queryKey: ['noti-clients'], queryFn: () => notiApi.listClients() })
  const [name, setName] = useState(w.name)
  const [description, setDescription] = useState(w.description)
  const [allowed, setAllowed] = useState<string[]>(w.allowedClients)

  const save = useMutation({
    mutationFn: () => notiApi.patchWorkflowV2(w.key, { name, description, allowedClients: allowed }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-workflow', w.key] }),
  })
  const patchFlag = useMutation({
    mutationFn: (p: { active?: boolean; critical?: boolean }) => notiApi.patchWorkflowV2(w.key, p),
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-workflow', w.key] }),
  })
  const remove = useMutation({
    mutationFn: () => notiApi.deleteWorkflow(w.key),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['noti-workflows'] })
      navigate({ to: '/workflows' })
    },
  })

  return (
    <>
      <div className="panel">
        <div className="form-grid">
          <label>ชื่อ workflow
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>คำอธิบาย
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label>เปิดใช้งาน (active)
            <div className="toggle-row">
              <Toggle checked={w.active} disabled={patchFlag.isPending} onChange={(v) => patchFlag.mutate({ active: v })} />
              <span className="muted small">ปิด = trigger ถูกบันทึกเป็น discarded ไม่มีการส่ง</span>
            </div>
          </label>
          <label>Critical
            <div className="toggle-row">
              <Toggle checked={w.critical} disabled={patchFlag.isPending} onChange={(v) => patchFlag.mutate({ critical: v })} />
              <span className="muted small">critical = ข้ามการปิดรับของผู้ใช้ (preferences) เสมอ</span>
            </div>
          </label>
        </div>

        <h3 style={{ marginTop: '1rem' }}>Client ต้นทางที่อนุญาตให้ยิง</h3>
        <p className="muted small" style={{ margin: '0 0 0.5rem' }}>
          ไม่เลือกเลย = ทุก client ที่ authenticate แล้วยิงได้ · เลือกแล้ว client นอกรายการได้ 403
        </p>
        <div className="chip-select">
          {(clients.data?.data ?? []).map((c) => {
            const on = allowed.includes(c.id)
            return (
              <button key={c.id} className={`chip ${on ? 'on' : ''}`}
                onClick={() => setAllowed(on ? allowed.filter((x) => x !== c.id) : [...allowed, c.id])}>
                {c.id}
              </button>
            )
          })}
        </div>
        {save.isError && <div className="error">{(save.error as Error).message}</div>}
        <button className="btn-primary" style={{ marginTop: '0.8rem' }} disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={15} /> บันทึก
        </button>
      </div>

      <div className="panel danger-zone">
        <h3>ลบ workflow</h3>
        <p className="muted small" style={{ margin: '0 0 0.6rem' }}>ลบได้เฉพาะ workflow ที่ยังไม่มีประวัติ events — ไม่เช่นนั้นให้ปิด active แทน</p>
        {remove.isError && <div className="error">{(remove.error as Error).message}</div>}
        <button className="btn-danger" disabled={remove.isPending} onClick={() => {
          if (confirm(`ยืนยันลบ workflow ${w.key}?`)) remove.mutate()
        }}>
          <Trash2 size={15} /> ลบ workflow นี้
        </button>
      </div>
    </>
  )
}

// ── Parameters: inbound payload contract + per-param defaults + audit fields ──
// The six reserved keys map straight into the transaction audit log (TOR ข้อ
// 9.2). A trigger that sends the key in payload wins; otherwise the param's
// default fills it; otherwise it is left empty/null.
const AUDIT_PARAM_FIELDS = [
  { key: 'role', label: 'Role', sub: 'Role ตอนทำรายการ' },
  { key: 'employeeId', label: 'Employee_id', sub: 'รหัสพนักงาน' },
  { key: 'action', label: 'Action', sub: 'การกระทำ' },
  { key: 'targetObject', label: 'Target_object', sub: 'สิ่งที่ถูกกระทำ' },
  { key: 'beforeValue', label: 'Before_value', sub: 'ก่อนเปลี่ยน' },
  { key: 'approvedBy', label: 'Approved_by', sub: 'คนอนุมัติ' },
] as const
const AUDIT_KEYS = new Set<string>(AUDIT_PARAM_FIELDS.map((f) => f.key))

function ParametersTab({ w }: { w: Workflow }) {
  const qc = useQueryClient()
  type Row = { key: string; description: string; example: string; required: boolean; default: string }
  const [rows, setRows] = useState<Row[]>([])
  const [audit, setAudit] = useState<Record<string, string>>({})

  useEffect(() => {
    const sch = w.payloadSchema ?? {}
    setRows(Object.entries(sch)
      .filter(([key]) => !AUDIT_KEYS.has(key))
      .map(([key, v]) => ({
        key, description: v.description ?? '', example: v.example ?? '', required: v.required ?? false, default: v.default ?? '',
      })))
    setAudit(Object.fromEntries(AUDIT_PARAM_FIELDS.map((f) => [f.key, sch[f.key]?.default ?? ''])))
  }, [w.key, w.updatedAt, w.payloadSchema])

  const save = useMutation({
    mutationFn: () => {
      const schema: PayloadSchema = {}
      for (const r of rows) {
        const k = r.key.trim()
        if (!k || AUDIT_KEYS.has(k)) continue
        schema[k] = { description: r.description, example: r.example, required: r.required, ...(r.default.trim() ? { default: r.default.trim() } : {}) }
      }
      for (const f of AUDIT_PARAM_FIELDS) {
        const def = (audit[f.key] ?? '').trim()
        if (def) schema[f.key] = { description: `Audit: ${f.sub}`, default: def }
      }
      return notiApi.patchWorkflowV2(w.key, { payloadSchema: schema })
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-workflow', w.key] }),
  })

  const set = (i: number, patch: Partial<Row>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  return (
    <div className="panel">
      <h3>พารามิเตอร์ขาเข้า (payload ของ trigger)</h3>
      <p className="muted small" style={{ margin: '0 0 0.7rem' }}>
        สัญญาข้อมูลที่ระบบต้นทางส่งมากับ <code>payload</code> — ใช้เป็นเอกสารและตัวแปร render (<code>{'{{.ชื่อตัวแปร}}'}</code>).
        <b> ค่าตั้งต้น</b> = ถ้า trigger ไม่ได้ส่ง key นั้นมา ระบบจะใช้ค่านี้แทน (ไม่ส่ง + ไม่มีค่าตั้งต้น = เว้นว่าง)
      </p>
      <table className="data-table param-table">
        <thead>
          <tr><th style={{ width: '16%' }}>Key</th><th>คำอธิบาย</th><th style={{ width: '19%' }}>ตัวอย่าง</th><th style={{ width: '19%' }}>ค่าตั้งต้น</th><th style={{ width: 64 }}>จำเป็น</th><th style={{ width: 36 }} /></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td><input value={r.key} spellCheck={false} onChange={(e) => set(i, { key: e.target.value })} /></td>
              <td><input value={r.description} onChange={(e) => set(i, { description: e.target.value })} /></td>
              <td><input value={r.example} onChange={(e) => set(i, { example: e.target.value })} /></td>
              <td><input value={r.default} spellCheck={false} placeholder="—" onChange={(e) => set(i, { default: e.target.value })} /></td>
              <td style={{ textAlign: 'center' }}>
                <input type="checkbox" checked={r.required} onChange={(e) => set(i, { required: e.target.checked })} />
              </td>
              <td><button className="icon-ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 size={14} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn-ghost" onClick={() => setRows([...rows, { key: '', description: '', example: '', required: false, default: '' }])}>
        <Plus size={14} /> เพิ่มพารามิเตอร์
      </button>

      <h3 style={{ marginTop: '1.5rem' }}>ค่าสำหรับ Audit Log (TOR ข้อ 9.2) — optional</h3>
      <p className="muted small" style={{ margin: '0 0 0.6rem' }}>
        คีย์สงวนที่ map เข้า audit log อัตโนมัติ — ตั้ง <b>ค่าตั้งต้น</b> ไว้ได้ ถ้า trigger ส่ง key เดียวกันมาใน <code>payload</code> จะใช้ค่าที่ส่งแทน
        (ไม่ส่ง + ไม่มีค่าตั้งต้น = เว้นว่าง/null). ส่วน User_id / Status / Module / วันเวลา / IP / อุปกรณ์ ระบบเติมให้อัตโนมัติ
      </p>
      <table className="data-table param-table">
        <thead>
          <tr><th style={{ width: '34%' }}>ฟิลด์ audit (payload key)</th><th>ค่าตั้งต้น</th></tr>
        </thead>
        <tbody>
          {AUDIT_PARAM_FIELDS.map((f) => (
            <tr key={f.key}>
              <td><code>{f.label}</code> <span className="muted small">{f.sub} · <code>{f.key}</code></span></td>
              <td><input value={audit[f.key] ?? ''} spellCheck={false} placeholder="ว่าง = null"
                onChange={(e) => setAudit({ ...audit, [f.key]: e.target.value })} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      {save.isError && <div className="error">{(save.error as Error).message}</div>}
      <div className="modal-actions" style={{ marginTop: '0.8rem' }}>
        <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={15} /> บันทึกสัญญาพารามิเตอร์
        </button>
        {save.isSuccess && !save.isPending && <span className="muted small" style={{ alignSelf: 'center' }}>บันทึกแล้ว</span>}
      </div>
    </div>
  )
}

// ── Destinations: routing + outbound param map ───────────────────────────────
type DestDraft = DestinationInput & { _id: number }

function DestinationsTab({ w }: { w: Workflow }) {
  const qc = useQueryClient()
  const clients = useQuery({ queryKey: ['noti-clients'], queryFn: () => notiApi.listClients() })
  const channels = useQuery({ queryKey: ['noti-channels'], queryFn: () => notiApi.listChannels() })
  const [drafts, setDrafts] = useState<DestDraft[]>([])

  useEffect(() => {
    setDrafts((w.destinations ?? []).map((d: Destination, i) => ({
      _id: i + 1, kind: d.kind, ref: d.ref, enabled: d.enabled,
      paramMap: d.paramMap, legacyChannel: d.legacyChannel,
    })))
  }, [w.key, w.updatedAt, w.destinations])

  const save = useMutation({
    mutationFn: () => notiApi.putDestinations(w.key, drafts.map(({ _id, ...d }) => d)),
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-workflow', w.key] }),
  })

  const set = (i: number, patch: Partial<DestDraft>) =>
    setDrafts(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)))

  // Default destinations to legacy (Templates) mode so the common
  // in-app/push/email flow never surfaces the param-map editor. sms/webhook
  // have no legacy template path → they stay param-map.
  const legacyFor = (kind: string, ref: string): string => {
    if (kind === 'client') return 'in_app'
    const type = channels.data?.data.find((c) => c.id === ref)?.type
    return type === 'email' ? 'email' : type === 'fcm' ? 'push' : type === 'mock' ? 'mock' : ''
  }

  const requiredHint = (d: DestDraft): string => {
    if (d.legacyChannel) return ''
    if (d.kind === 'client') return 'key ที่จำเป็น: title, body · เสริม: deepLink'
    const type = channels.data?.data.find((c) => c.id === d.ref)?.type
    switch (type) {
      case 'email': return 'key ที่จำเป็น: subject, body · ผู้รับ: to (เช่น {{.payload.email}})'
      case 'sms': return 'key ที่จำเป็น: to, text'
      case 'fcm': return 'key ที่จำเป็น: title, body · เสริม: data.*'
      case 'webhook': return 'ทุก key ถูกส่งเป็น JSON message ไปยัง endpoint'
      default: return ''
    }
  }

  return (
    <>
      <div className="panel">
        <h3>ปลายทางการส่ง (Destinations)</h3>
        <p className="muted small" style={{ margin: '0 0 0.8rem' }}>
          เมื่อ engine รับ event ของ workflow นี้ จะส่งต่อไปยังปลายทางทุกแถวที่เปิดอยู่ —
          ปลายทางเป็นได้ทั้ง <b>Client app</b> (แจ้งเตือน in-app ในแอปนั้น) หรือ <b>Channel</b>
          (Email / SMS / Push / API) · หัวข้อ/เนื้อหากำหนด <b>รายปลายทาง</b> ที่แท็บ <b>Templates</b>,
          ผู้รับของ Email มาจาก <code>payload.email</code> อัตโนมัติ (หรือกำหนด <code>to</code> ที่แท็บ Templates)
        </p>

        {drafts.map((d, i) => (
          <div className="dest-card" key={d._id}>
            <div className="dest-head">
              <span className="dest-no">{i + 1}</span>
              <select value={d.kind} onChange={(e) => { const kind = e.target.value as 'client' | 'channel'; set(i, { kind, ref: '', legacyChannel: legacyFor(kind, '') }) }}>
                <option value="client">Client app (in-app)</option>
                <option value="channel">Channel</option>
              </select>
              <select value={d.ref} onChange={(e) => set(i, { ref: e.target.value, legacyChannel: legacyFor(d.kind, e.target.value) })}>
                <option value="" disabled>— เลือกปลายทาง —</option>
                {d.kind === 'client'
                  ? (clients.data?.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.id})</option>)
                  : (channels.data?.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name} ({CHANNEL_META[c.type].label})</option>
                  ))}
              </select>
              <span className="dest-icons">{d.kind === 'client' ? <AppWindow size={15} /> : <Radio size={15} />}</span>
              {d.legacyChannel && <span className="pill">โหมดเดิม: templates/{d.legacyChannel}</span>}
              <span className="dest-spacer" />
              <Toggle checked={d.enabled ?? true} onChange={(v) => set(i, { enabled: v })} />
              <button className="icon-ghost" aria-label="ลบปลายทาง" onClick={() => setDrafts(drafts.filter((_, j) => j !== i))}>
                <Trash2 size={15} />
              </button>
            </div>
            {d.legacyChannel ? (
              <div className="dest-legacy muted small">
                ตั้งหัวข้อ/เนื้อหา <b>รายปลายทาง</b> ที่แท็บ <b>Templates</b> (เก็บใน paramMap
                ของปลายทางนี้ · ว่าง = ใช้ template กลางเดิม) · ผู้รับ Email/SMS มาจาก
                <code>payload.email</code> / <code>payload.phone</code> อัตโนมัติ
              </div>
            ) : (
              <>
                <div className="muted small" style={{ margin: '0.3rem 0' }}>{requiredHint(d)}</div>
                <KvEditor
                  rows={(d.paramMap ?? []) as ParamKV[]}
                  onChange={(rows) => set(i, { paramMap: rows })}
                  keyPlaceholder="to / subject / body / data.xxx"
                  valuePlaceholder="ค่าคงที่ หรือ template เช่น {{.payload.email}}"
                />
              </>
            )}
          </div>
        ))}

        <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
          <button className="btn-ghost" onClick={() => setDrafts([...drafts, { _id: Date.now(), kind: 'channel', ref: '', enabled: true, paramMap: [] }])}>
            <Plus size={14} /> เพิ่มปลายทาง
          </button>
          <button className="btn-primary" disabled={save.isPending || drafts.some((d) => !d.ref)} onClick={() => save.mutate()}>
            <Save size={15} /> บันทึกปลายทางทั้งชุด
          </button>
        </div>
        {save.isError && <div className="error">{(save.error as Error).message}</div>}
      </div>

      <div className="panel">
        <h3>ตัวแปรที่ใช้ได้ใน template</h3>
        <ul className="muted small var-list">
          <li><code>{'{{.ชื่อตัวแปร}}'}</code> — ค่าจาก payload ขาเข้า (ตามแท็บ Parameters)</li>
          <li><code>{'{{.payload.x}}'}</code> — รูปแบบระบุ namespace ชัดเจน</li>
          <li><code>{'{{.subscriber.ref}}'}</code> · <code>{'{{.subscriber.type}}'}</code> · <code>{'{{.subscriber.locale}}'}</code></li>
          <li><code>{'{{.event.transactionId}}'}</code> · <code>{'{{.event.workflowKey}}'}</code> · <code>{'{{.event.source}}'}</code></li>
        </ul>
      </div>
    </>
  )
}

// ── Templates: per-destination message editor ────────────────────────────────
// One sub-tab per destination (labeled by its name, not the channel type). The
// friendly subject/body/to fields are stored in THAT destination's param_map
// using channel-appropriate keys, so two destinations of the same channel type
// can carry different messages. The engine renders from param_map when present
// and falls back to the central templates table only while a backfilled
// destination's param_map is still empty (see worker.go).
type Transport = 'in_app' | 'email' | 'push' | 'sms' | 'webhook' | 'mock' | 'unknown'

function transportOf(d: { kind: string; ref: string }, channels: readonly { id: string; type: string }[]): Transport {
  if (d.kind === 'client') return 'in_app'
  const t = channels.find((c) => c.id === d.ref)?.type
  return t === 'email' ? 'email' : t === 'fcm' ? 'push' : t === 'sms' ? 'sms'
    : t === 'webhook' ? 'webhook' : t === 'mock' ? 'mock' : 'unknown'
}

// Per-transport field shape. subjectKey is the param the subject field writes
// into (in-app/push render it as the title); needTo surfaces a recipient field
// (email optional → falls back to payload.email; sms required).
const FIELD_CONFIG: Record<Transport, {
  subjectKey?: 'title' | 'subject'; subjectLabel?: string; bodyLabel: string
  needTo?: 'email' | 'sms'; note?: string
}> = {
  in_app: { subjectKey: 'title', subjectLabel: 'หัวข้อ (title)', bodyLabel: 'เนื้อหา (body)', note: 'ปุ่ม deep link มาจาก payload.cta ตอนยิง' },
  push: { subjectKey: 'title', subjectLabel: 'หัวข้อ (title)', bodyLabel: 'เนื้อหา (body)', note: 'ปุ่ม deep link มาจาก payload.cta · ฟิลด์ data.* ตั้งที่แท็บ Destinations' },
  email: { subjectKey: 'subject', subjectLabel: 'หัวข้อ (subject)', bodyLabel: 'เนื้อหา (body)', needTo: 'email' },
  sms: { bodyLabel: 'เนื้อหา (text)', needTo: 'sms' },
  webhook: { subjectKey: 'subject', subjectLabel: 'หัวข้อ (subject)', bodyLabel: 'เนื้อหา (body)', note: 'ทุก key ถูกส่งเป็น JSON message · คีย์อื่นแก้ที่แท็บ Destinations' },
  mock: { subjectKey: 'subject', subjectLabel: 'หัวข้อ (subject)', bodyLabel: 'เนื้อหา (body)' },
  unknown: { subjectKey: 'subject', subjectLabel: 'หัวข้อ (subject)', bodyLabel: 'เนื้อหา (body)' },
}

// setParam upserts one key in an ordered param_map, preserving position and
// every other key. An empty value drops the key (so clearing reverts to the
// central template).
function setParam(rows: ParamKV[], key: string, value: string): ParamKV[] {
  if (!value.trim()) return rows.filter((r) => r.key !== key)
  if (rows.some((r) => r.key === key)) return rows.map((r) => (r.key === key ? { key, value } : r))
  return [...rows, { key, value }]
}

function TemplatesTab({ w }: { w: Workflow }) {
  const clients = useQuery({ queryKey: ['noti-clients'], queryFn: () => notiApi.listClients() })
  const channels = useQuery({ queryKey: ['noti-channels'], queryFn: () => notiApi.listChannels() })
  const dests = w.destinations ?? []
  const [activeId, setActiveId] = useState<number>(dests[0]?.id ?? 0)

  useEffect(() => {
    if (!dests.some((d) => d.id === activeId)) setActiveId(dests[0]?.id ?? 0)
  }, [w.updatedAt, w.destinations]) // eslint-disable-line react-hooks/exhaustive-deps

  const chList = channels.data?.data ?? []
  const clList = clients.data?.data ?? []
  const nameOf = (d: Destination) => d.kind === 'client'
    ? (clList.find((c) => c.id === d.ref)?.name ?? d.ref)
    : (chList.find((c) => c.id === d.ref)?.name ?? d.ref)
  const chipOf = (d: Destination): string => {
    if (d.kind === 'client') return 'In-app'
    const t = chList.find((c) => c.id === d.ref)?.type
    return t ? CHANNEL_META[t].label : 'channel'
  }
  const active = dests.find((d) => d.id === activeId)

  return (
    <div className="panel">
      <h3><FileText size={16} /> ข้อความต่อปลายทาง (Templates)</h3>
      <p className="muted small" style={{ margin: '0 0 0.7rem' }}>
        แต่ละปลายทางมีหัวข้อ/เนื้อหาของตัวเอง — เก็บลงใน param map ของปลายทางนั้น
        (ปลายทางชนิดเดียวกันหลายตัวจึงมีข้อความต่างกันได้) · เว้นว่างทั้งหมด = กลับไปใช้ template กลางเดิม
      </p>
      {dests.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>workflow นี้ยังไม่มีปลายทาง — เพิ่มที่แท็บ Destinations ก่อน</p>
      ) : (
        <>
          <div className="tabs sub-tabs">
            {dests.map((d) => (
              <button key={d.id} className={d.id === activeId ? 'active' : ''} title={`${d.kind}: ${d.ref}`}
                onClick={() => setActiveId(d.id)}>
                {nameOf(d)} <span className="muted small">· {chipOf(d)}{d.enabled ? '' : ' · ปิด'}</span>
              </button>
            ))}
          </div>
          {active && <DestinationTemplateEditor key={active.id} w={w} dest={active} transport={transportOf(active, chList)} />}
        </>
      )}
    </div>
  )
}

function DestinationTemplateEditor({ w, dest, transport }: { w: Workflow; dest: Destination; transport: Transport }) {
  const qc = useQueryClient()
  const cfg = FIELD_CONFIG[transport]
  const bodyKey = transport === 'sms' ? 'text' : 'body' // sms adapter reads `text`
  const pm = dest.paramMap ?? []
  const paramVal = (k: string) => pm.find((r) => r.key === k)?.value ?? ''
  const pmSubject = cfg.subjectKey ? paramVal(cfg.subjectKey) : ''
  const pmBody = paramVal(bodyKey)
  const pmTo = paramVal('to')
  const hasParamContent = !!(pmSubject || pmBody || pmTo)

  // A backfilled destination whose param_map is still empty pre-fills from the
  // central template, so saving never loses the seeded copy.
  const legacyChannel = dest.legacyChannel || ''
  const wantTemplate = !hasParamContent && !!legacyChannel
  const tpl = useQuery({
    queryKey: ['noti-template', w.key, legacyChannel],
    queryFn: () => notiApi.getTemplate(w.key, legacyChannel as Channel),
    enabled: wantTemplate,
    retry: false, // a 404 (this workflow has no central template) is an answer, not a retry
  })
  const tplHasContent = !!(tpl.data && (tpl.data.subject || tpl.data.body))

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [to, setTo] = useState('')

  useEffect(() => {
    if (hasParamContent || !wantTemplate) {
      setSubject(pmSubject); setBody(pmBody); setTo(pmTo)
    } else if (tpl.data) {
      setSubject(cfg.subjectKey ? tpl.data.subject : '')
      setBody(tpl.data.body); setTo(pmTo)
    }
  }, [dest.id, dest.updatedAt, tpl.data]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => {
      const inputs: DestinationInput[] = (w.destinations ?? []).map((d) => {
        const base = { kind: d.kind, ref: d.ref, enabled: d.enabled, legacyChannel: d.legacyChannel }
        if (d.id !== dest.id) return { ...base, paramMap: d.paramMap ?? [] }
        let next = d.paramMap ?? []
        if (cfg.subjectKey) next = setParam(next, cfg.subjectKey, subject)
        next = setParam(next, bodyKey, body)
        if (cfg.needTo) next = setParam(next, 'to', to)
        return { ...base, paramMap: next }
      })
      return notiApi.putDestinations(w.key, inputs)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-workflow', w.key] }),
  })

  return (
    <div className="form-grid" style={{ marginTop: '0.6rem' }}>
      {wantTemplate && tpl.isLoading && (
        <p className="muted small" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0 0 0.2rem' }}>
          <Loader2 size={14} className="spin" /> กำลังโหลดข้อความเดิม…
        </p>
      )}
      {cfg.needTo && (
        <label>ผู้รับ (to){cfg.needTo === 'email' ? ' — เว้นว่าง = ใช้ payload.email' : ''}
          <input value={to} spellCheck={false}
            placeholder={cfg.needTo === 'sms' ? 'เช่น {{.payload.phone}}' : 'เช่น {{.payload.email}} (เว้นว่างได้)'}
            onChange={(e) => setTo(e.target.value)} />
        </label>
      )}
      {cfg.subjectKey && (
        <label>{cfg.subjectLabel}
          <input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </label>
      )}
      <label>{cfg.bodyLabel}
        <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
      </label>
      {cfg.note && <p className="muted small" style={{ margin: '-0.2rem 0 0' }}>{cfg.note}</p>}
      {wantTemplate && tplHasContent && (
        <p className="muted small" style={{ margin: '-0.2rem 0 0' }}>
          ค่าเริ่มต้นดึงจาก template กลางเดิม — กดบันทึกเพื่อผูกเป็นข้อความของปลายทางนี้
        </p>
      )}
      {wantTemplate && !tpl.isLoading && !tplHasContent && (
        <p className="muted small" style={{ margin: '-0.2rem 0 0' }}>
          ยังไม่มีข้อความเดิมสำหรับปลายทางนี้ — พิมพ์ข้อความแล้วกดบันทึก
        </p>
      )}
      {save.isError && <div className="error">{(save.error as Error).message}</div>}
      <div>
        <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={15} /> บันทึกข้อความปลายทางนี้
        </button>
        {save.isSuccess && !save.isPending && <span className="muted small" style={{ marginLeft: '0.6rem' }}>บันทึกแล้ว</span>}
      </div>
    </div>
  )
}

// ── cURL: ready-to-run trigger example, generated from THIS workflow's
// Parameters + Destinations (email workflows get payload.email + attachment). ──
const ATTACH_EXAMPLE_URL = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'

function CurlTab({ w }: { w: Workflow }) {
  const channels = useQuery({ queryKey: ['noti-channels'], queryFn: () => notiApi.listChannels() })
  const [withAttachment, setWithAttachment] = useState(true)
  const [copied, setCopied] = useState(false)

  const chanType = (ref: string) => channels.data?.data.find((c) => c.id === ref)?.type
  const dests = (w.destinations ?? []).filter((d) => d.enabled)
  const hitsEmail = dests.some((d) => d.legacyChannel === 'email' || chanType(d.ref) === 'email')
  const hitsSms = dests.some((d) => d.legacyChannel === 'sms' || chanType(d.ref) === 'sms')
  // in-app feed (kind=client) and push (fcm) render an actionable deep-link
  // button from payload.cta — see engine inapp.go/push.go.
  const hitsInApp = dests.some((d) => d.kind === 'client' || d.legacyChannel === 'in_app')
  const hitsPush = dests.some((d) => d.legacyChannel === 'push' || chanType(d.ref) === 'fcm')
  const actionable = hitsInApp || hitsPush

  // where this workflow actually sends — shown so the example reads as "analysed"
  const outLabels = dests.map((d) => {
    if (d.kind === 'client') return `in-app (${d.ref})`
    const t = chanType(d.ref)
    return t ? CHANNEL_META[t].label : d.ref
  })

  // example recipient — customer by domain; broadcasts target a topic instead
  const isBroadcast = w.key.includes('broadcast')
  const custRef = w.key.startsWith('govpocket') ? 'CIF-0012345678' : 'CIF-6900001'
  const to: unknown[] = isBroadcast
    ? [{ type: 'topic', topicKey: 'all-loancard-customers' }]
    : [{ subscriberId: custRef }]

  // payload from the documented Parameters (incl. the reserved audit keys, ข้อ
  // 9.2). Prefer the param's example, then its default, then a <placeholder> so
  // the body is copy-paste runnable rather than literal "<role>".
  const payload: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(w.payloadSchema ?? {})) payload[k] = v.example || v.default || `<${k}>`
  if (hitsEmail && !payload.email) payload.email = 'customer@example.com'
  if (hitsSms && !payload.phone) payload.phone = '0812345678'
  // payload.cta = the deep-link button for in-app/push (object, so it can't live
  // in the flat Parameters schema). deepLink is derived from the workflow key.
  if (actionable && !('cta' in payload)) {
    const [domain, area] = w.key.split('.')
    payload.cta = { deepLink: `baacmobile://${domain}/${area ?? ''}`, label: 'ดูรายละเอียด' }
  }

  const source = w.allowedClients.find((c) => c !== 'portal') ?? w.allowedClients[0] ?? 'lms'
  const txn = `${source.toUpperCase()}-2026-000001`

  const body: Record<string, unknown> = { name: w.key, to, payload }
  if (hitsEmail && withAttachment) {
    body.overrides = { email: { attachments: [{ name: 'contract.pdf', url: ATTACH_EXAMPLE_URL }] } }
  }
  body.transactionId = txn

  const curl = `curl -X POST ${window.location.origin}/v1/events/trigger \\
  -H "Authorization: ApiKey <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(body, null, 2)}'`

  const copy = () => {
    navigator.clipboard.writeText(curl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <div className="panel">
      <h3><Terminal size={16} /> ตัวอย่างการยิงด้วย cURL</h3>
      <p className="muted small" style={{ margin: '0 0 0.5rem' }}>
        ปั้นจากแท็บ <b>Parameters</b> + <b>Destinations</b> ของ workflow นี้ — คัดลอกไปวางได้เลย
        เปลี่ยน <code>&lt;YOUR_API_KEY&gt;</code> เป็น ApiKey ของ client ต้นทาง (เช่น <code>{source}</code>)
        แล้วปรับค่าตัวอย่างให้ตรงกับธุรกรรมจริง
      </p>
      <p className="small" style={{ margin: '0 0 0.6rem', padding: '0.45rem 0.7rem', borderLeft: '3px solid var(--warn)', background: 'var(--surface-2)', borderRadius: '0 6px 6px 0' }}>
        ⚠️ <code>ncs_…</code> คือ <b>client secret</b> — ใส่ที่ <code>X-Client-Id</code> + <code>X-Client-Secret</code>
        {' '}ไม่ใช่ <code>Authorization: ApiKey</code> (คนละระบบ ถ้าเอา <code>ncs_…</code> ใส่ใน ApiKey จะได้ <code>unknown API key</code>)
      </p>
      <p className="muted small" style={{ margin: '0 0 0.6rem' }}>
        ช่องทางที่ส่ง: <b>{outLabels.length ? outLabels.join(' · ') : '— (ยังไม่มีปลายทาง)'}</b>
        {hitsEmail && ' · ผู้รับอีเมลมาจาก payload.email (ใช้ก่อน UMS)'}
        {actionable && ' · payload.cta = ปุ่ม deep link (in-app/push)'}
      </p>
      <p className="muted small" style={{ margin: '0 0 0.6rem' }}>
        รวมคีย์สงวนสำหรับ <b>Audit (TOR ข้อ 9.2)</b> — <code>role · action · employeeId · targetObject · beforeValue · approvedBy</code>
        {' '}— ส่งมาจะลงใน audit ของธุรกรรม (ไม่ส่งจะใช้ค่าตั้งต้นของ param นั้น)
      </p>
      {hitsEmail && (
        <label className="muted small" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0 0 0.6rem' }}>
          <input type="checkbox" checked={withAttachment} onChange={(e) => setWithAttachment(e.target.checked)} />
          แนบไฟล์ตัวอย่าง (<code>overrides.email.attachments</code>) — url ต้องเปิดได้จริง (HTTP 200) และ ≤ 5 MB
        </label>
      )}
      <pre className="code-block">{curl}</pre>
      <button className="btn-ghost" style={{ marginTop: '0.6rem' }} onClick={copy}>
        <Copy size={14} /> {copied ? 'คัดลอกแล้ว' : 'คัดลอก cURL'}
      </button>
      {isBroadcast && (
        <p className="muted small" style={{ marginTop: '0.6rem' }}>
          workflow แบบ broadcast ส่งถึงสมาชิกใน topic — สร้าง/เพิ่มสมาชิก topic ก่อน (ดู API Docs → topics)
        </p>
      )}
    </div>
  )
}
